import React, { useMemo, useState } from 'react';
import { WorldSection } from './WorldPageHeader';
import { CopyTextButton } from './CopyTextButton';
import { ItemThumb } from './ItemSprite';
import { ITEM_SHEET_EXPECTED_PX, ITEM_SHEET_GRID, alternateSheetUploads, itemSpriteSheets } from '../../data/guide/itemSpriteSheets';
import { excludedSpriteIds, indexSchema, indexSpotChecks, missingSpriteIds } from '../../data/guide/itemSpriteIndex';
import { spriteIndex } from '../../utils/itemSprites';
import { itemSpriteBounds } from '../../data/guide/itemSpriteBounds';
import { analyzeSheet, catalogImageGaps, frameIssueLabel, staticFrameIssues } from '../../utils/itemSpriteDiagnostics';
import type { FrameIssue, SheetAnalysis } from '../../utils/itemSpriteDiagnostics';
import { itemName } from '../../utils/worldFormat';
import type { WorldData } from '../../types/world';

/** Casos de encuadre: Scroll of Flame se busca por nombre exacto en el catálogo; el resto por ID. */
function framingCases(w: WorldData): {
  label: string;
  id?: string;
}[] {
  const flame = Array.from(w.items.keys()).find(id => itemName(w, id).trim().toLowerCase() === 'scroll of flame');
  return [{
    label: 'Scroll of Flame',
    id: flame
  }, {
    label: 'Scroll of Evil Spirit',
    id: w.items.has('15,8') ? '15,8' : undefined
  }, {
    label: 'Dragon Helm',
    id: w.items.has('7,1') ? '7,1' : undefined
  }, {
    label: 'Espada (0,5)',
    id: w.items.has('0,5') ? '0,5' : undefined
  }, {
    label: 'Alas (12,3)',
    id: w.items.has('12,3') ? '12,3' : undefined
  }, {
    label: 'Lámina 02 · arco (4,5)',
    id: w.items.has('4,5') ? '4,5' : undefined
  }];
}
const describe = (a: SheetAnalysis) => {
  if (!a.decoded) return `NO CARGA: ${a.loadError}`;
  const cw = a.width! / ITEM_SHEET_GRID;
  const ch = a.height! / ITEM_SHEET_GRID;
  const full = a.width === ITEM_SHEET_EXPECTED_PX && a.height === ITEM_SHEET_EXPECTED_PX;
  const res = full ? 'resolución completa' : `PERDIÓ RESOLUCIÓN (declarada ${ITEM_SHEET_EXPECTED_PX} x ${ITEM_SHEET_EXPECTED_PX})`;
  return `descargada y decodificada · ${a.width} x ${a.height} · celda ${cw} x ${ch}${a.width === a.height ? '' : ' · NO CUADRADA'} · ${res}`;
};
const issueLine = (w: WorldData, i: FrameIssue) => `${i.id}${w.items.has(i.id) ? ` ${itemName(w, i.id)}` : ''} · lámina ${i.sheet} col ${i.column} fila ${i.row} · ${frameIssueLabel[i.kind]} · ${i.detail}`;

/** Panel del staff: carga de láminas, encuadres y huecos del catálogo, en categorías separadas. */
export function ItemSheetCheck({
  w
}: {
  w: WorldData;
}) {
  const [results, setResults] = useState<SheetAnalysis[]>([]);
  const [alts, setAlts] = useState<SheetAnalysis[]>([]);
  const [running, setRunning] = useState<string | null>(null);
  const run = async () => {
    const out: SheetAnalysis[] = [];
    for (const s of itemSpriteSheets) {
      setRunning(`Analizando lámina ${s.id}…`);
      out.push(await analyzeSheet(Number(s.id), s.url));
      setResults([...out]);
    }
    setRunning('Midiendo subidas alternativas…');
    setAlts(await Promise.all(alternateSheetUploads.map(a => analyzeSheet(-1, a.url))));
    setRunning(null);
  };
  const stats = useMemo(() => {
    const indexIds = Array.from(spriteIndex.keys());
    const inCatalog = indexIds.filter(id => w.items.has(id));
    const notInCatalog = indexIds.filter(id => !w.items.has(id));
    const missingInCatalog = missingSpriteIds.filter(id => w.items.has(id));
    const excludedLeaked = excludedSpriteIds.filter(id => w.items.has(id));
    const spot = indexSpotChecks.map(c => {
      const got = spriteIndex.get(c.id);
      const ok = !!got && got.sheet === c.sheet && got.column === c.column && got.row === c.row;
      return {
        ...c,
        got,
        ok,
        catalogName: w.items.has(c.id) ? itemName(w, c.id) : undefined
      };
    });
    return {
      indexIds,
      inCatalog,
      notInCatalog,
      missingInCatalog,
      excludedLeaked,
      spot,
      gaps: catalogImageGaps(w),
      staticIssues: staticFrameIssues()
    };
  }, [w]);
  const failedSheets = results.filter(r => !r.decoded);
  const failedItems = Array.from(spriteIndex.entries()).filter(([, c]) => failedSheets.some(f => f.sheet === c.sheet)).map(([id]) => id);
  const pixelIssues = results.flatMap(r => r.issues);
  const frameIssues = [...stats.staticIssues, ...pixelIssues];
  const notAnalyzable = results.filter(r => r.decoded && !r.analyzable);
  const analyzed = results.length === itemSpriteSheets.length;
  const categories = [{
    key: 'load',
    title: 'Imagen que no carga',
    count: analyzed ? failedItems.length : undefined,
    note: analyzed ? failedSheets.length ? `Láminas: ${failedSheets.map(f => `${String(f.sheet).padStart(2, '0')} (${f.loadError})`).join(', ')}` : 'Las 15 láminas se descargaron y decodificaron.' : 'Pulsa «Analizar las 15 láminas».'
  }, {
    key: 'frame',
    title: 'Recorte o posición incorrectos',
    count: analyzed ? frameIssues.length : stats.staticIssues.length,
    note: analyzed ? notAnalyzable.length ? `Sin lectura de píxeles en ${notAnalyzable.length} lámina(s): solo se comprobó el archivo de encuadre.` : 'Encuadres comparados con el contenido real de cada celda.' : 'Por ahora solo el archivo de encuadre; el análisis de píxeles requiere pulsar el botón.'
  }, {
    key: 'noIndex',
    title: 'Ítem sin imagen en el índice',
    count: stats.gaps.noIndex.length,
    note: 'Ítems publicados sin entrada en el índice compacto: marcador neutro.'
  }, {
    key: 'variant',
    title: 'Variante sin imagen específica',
    count: stats.gaps.variantBase.length,
    note: `Usan la imagen del ítem base, rotulada. Otras ${stats.gaps.variantNoImage.length} variantes no tienen imagen ni en su base.`
  }];
  const report = [`Índice: ${stats.indexIds.length} IDs (declarados ${indexSchema.totalItems}) en ${indexSchema.sheets} láminas. Coinciden con el catálogo publicado: ${stats.inCatalog.length} · no publicados: ${stats.notInCatalog.length}${stats.notInCatalog.length ? ` (${stats.notInCatalog.join(', ')})` : ''}.`, `De la lista missing del índice, publicados: ${stats.missingInCatalog.length}. Excluidos presentes en el catálogo: ${stats.excludedLeaked.length ? stats.excludedLeaked.join(', ') : 'ninguno'}.`, '', 'Láminas (descarga, decodificación y tamaño servido):', ...itemSpriteSheets.map(s => {
    const r = results.find(x => x.sheet === Number(s.id));
    return `Lámina ${s.id} · ${r ? describe(r) : 'sin comprobar'}${r?.decoded ? ` · ${r.analyzable ? `${r.checked} celdas analizadas` : 'píxeles no legibles (CORS)'}` : ''} · ${s.url}`;
  }), ...alternateSheetUploads.map((a, i) => `${a.note} (no se usa) · ${alts[i] ? describe(alts[i]) : 'sin comprobar'}`), '', `1. Imagen que no carga: ${analyzed ? `${failedItems.length} ítems${failedItems.length ? ` (${failedItems.join(', ')})` : ''}` : 'sin comprobar'}`, `2. Recorte o posición incorrectos: ${analyzed ? frameIssues.length : `${stats.staticIssues.length} (solo archivo de encuadre; píxeles sin comprobar)`}`, ...frameIssues.map(i => `   - ${issueLine(w, i)}`), `3. Ítem sin imagen en el índice: ${stats.gaps.noIndex.length}${stats.gaps.noIndex.length ? ` (${stats.gaps.noIndex.join(', ')})` : ''}`, `4. Variante sin imagen específica (usa la del ítem base): ${stats.gaps.variantBase.length}${stats.gaps.variantBase.length ? ` (${stats.gaps.variantBase.join(', ')})` : ''}`, `   Variantes sin imagen ni en su base: ${stats.gaps.variantNoImage.length}${stats.gaps.variantNoImage.length ? ` (${stats.gaps.variantNoImage.join(', ')})` : ''}`, '', 'Revisión visual: pendiente; la correspondencia es numérica por ID. Las imágenes no confirman drops, estadísticas, recetas ni pruebas en el juego.'].join('\n');
  return <WorldSection id="laminas-items" title="Imágenes de ítems (láminas)" count={stats.inCatalog.length} data-id="element-1485" data-mp="1cc29u7k5f00l">
      <p className="mb-3 max-w-3xl text-sm text-gray-400" data-id="element-1486" data-mp="17e0sps6a9arr">
        Cada lámina se divide en 8 x 8 según su tamaño real y se muestra solo cuando está descargada y decodificada por completo. Una falla de carga nunca se cuenta como «ítem sin imagen». La correspondencia es numérica por ID; la revisión visual es aparte.
      </p>

      <ol className="mb-5 grid gap-2 sm:grid-cols-2" data-id="element-1487" data-mp="18dtmdvc62bb9">
        {categories.map((c, i) => <li key={c.key} className="rounded-xl border border-white/10 p-3" data-id="element-1488" data-mp="1d63i5rzyq5lg" data-mpi={i}>
            <p className="flex items-baseline justify-between gap-3" data-id="element-1489" data-mp="ygxik2x7qdd1">
              <span className="text-sm font-semibold text-white" data-id="element-1490" data-mp="13fi9tcuhlmpc">{i + 1}. {c.title}</span>
              <span className="text-lg font-semibold text-white" data-id="element-1491" data-mp="13d5u76c7bofg">{c.count ?? '—'}</span>
            </p>
            <p className="mt-1 text-xs text-gray-400" data-id="element-1492" data-mp="1r3a0jlgry2a5">{c.note}</p>
          </li>)}
      </ol>

      <div className="mb-4 flex flex-wrap items-center gap-2" data-id="element-1493" data-mp="1w1tilas8ytx8">
        <button type="button" onClick={run} disabled={!!running} className="rounded-full border border-white/15 px-3 py-1.5 text-sm text-gray-200 transition-colors duration-150 hover:text-white disabled:opacity-50" data-id="element-1494" data-mp="1i3v8ihkyljnj">
          {running ?? 'Analizar las 15 láminas'}
        </button>
        <CopyTextButton text={report} label="Copiar comprobación de imágenes de ítems" data-id="element-1495" data-mp="arw5g2dfqe1f" />
      </div>

      {frameIssues.length > 0 && <div className="mb-5" data-id="element-1496" data-mp="39estna9ij48">
          <h3 className="mb-2 text-sm font-semibold text-white" data-id="element-1497" data-mp="3udvc2dzas7vp">Recorte o posición incorrectos</h3>
          <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1498" data-mp="d586xb3mqko4">
            {frameIssues.slice(0, 60).map(i => <li key={`${i.id}-${i.kind}`} className="flex items-center gap-3 rounded-xl border border-white/10 p-3" data-id="element-1499" data-mp="85vkgrgo7fm2">
                <ItemThumb id={i.id} name={w.items.has(i.id) ? itemName(w, i.id) : i.id} size="card" placeholder data-id="element-1500" data-mp="3gn9sso5xlvxa" />
                <div className="min-w-0 text-sm" data-id="element-1501" data-mp="3boos6nmrpa8b">
                  <p className="font-semibold text-white" data-id="element-1502" data-mp="3qbrr8yqqnqxr">{w.items.has(i.id) ? itemName(w, i.id) : `${i.id} (no publicado)`} <span className="font-normal text-gray-500" data-id="element-1503" data-mp="3lkrtxsdpj6uq">· {i.id}</span></p>
                  <p className="text-amber-300" data-id="element-1504" data-mp="2xvqqkjjqelwb">{frameIssueLabel[i.kind]}</p>
                  <p className="text-xs text-gray-500" data-id="element-1505" data-mp="2xtmevfflcilh">Lámina {i.sheet}, col {i.column}, fila {i.row} · {i.detail}</p>
                </div>
              </li>)}
          </ul>
          {frameIssues.length > 60 && <p className="mt-2 text-xs text-gray-500" data-id="element-1506" data-mp="ernv6tvs0m8e">Y {frameIssues.length - 60} más en el texto copiado.</p>}
        </div>}

      <h3 className="mb-2 text-sm font-semibold text-white" data-id="element-1507" data-mp="5ssn1c96922g">Casos de prueba</h3>
      <ul className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1508" data-mp="u7l98e9uabw">
        {[...stats.spot.slice(0, 4).map(s => ({
        key: s.id,
        id: s.id,
        name: s.catalogName ?? `${s.label} (no publicado)`,
        note: `Lámina ${s.sheet}, col ${s.column}, fila ${s.row} · ${s.ok ? 'posición coincide con el índice' : 'no coincide'}`,
        ok: s.ok
      })), ...framingCases(w).map(c => ({
        key: c.label,
        id: c.id,
        name: c.id ? itemName(w, c.id) : `${c.label}: no encontrado en el catálogo`,
        note: `${c.label} · ${c.id && itemSpriteBounds[c.id] ? `encuadre ${itemSpriteBounds[c.id].join(', ')}` : 'sin encuadre: celda completa'}`,
        ok: true
      }))].map(c => <li key={c.key} className="flex items-center gap-3 rounded-xl border border-white/10 p-3" data-id="element-1509" data-mp="34bze0p08eyb5">
            <ItemThumb id={c.id} name={c.name} size="card" placeholder lazy={false} data-id="element-1510" data-mp="2u67rno1x9vcj" />
            <div className="min-w-0 text-sm" data-id="element-1511" data-mp="2z5awb7dzpzjz">
              <p className="font-semibold text-white" data-id="element-1512" data-mp="2z86w719eeyb2">{c.name}{c.id && <span className="font-normal text-gray-500" data-id="element-1513" data-mp="3468h7rqdx7p6"> · {c.id}</span>}</p>
              <p className={`text-xs ${c.ok ? 'text-gray-400' : 'text-red-300'}`} data-id="element-1514" data-mp="394tfhlm2thaz">{c.note}</p>
            </div>
          </li>)}
      </ul>

      <div className="overflow-x-auto rounded-2xl border border-white/10" data-id="element-1515" data-mp="2biclap8n3jtg">
        <table className="w-full min-w-[640px] text-left text-sm" data-id="element-1516" data-mp="2ggnses7jvg7z">
          <thead className="bg-white/[0.03] text-xs text-gray-400" data-id="element-1517" data-mp="2lez478heo0su">
            <tr data-id="element-1518" data-mp="2lcmeua9bio5r">
              <th className="px-4 py-3 font-semibold" data-id="element-1519" data-mp="2q5fj6x0vjuhb">Lámina</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1520" data-mp="2q6ql0rares5s">Carga y tamaño servido</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1521" data-mp="1sp8qh0jor0x9">Celdas</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1522" data-mp="1xolaldj69tw5">URL</th>
            </tr>
          </thead>
          <tbody data-id="element-1523" data-mp="1po5kb2n0rtb8">
            {itemSpriteSheets.map(s => {
            const r = results.find(x => x.sheet === Number(s.id));
            const ok = r?.decoded && r.width === ITEM_SHEET_EXPECTED_PX && r.height === ITEM_SHEET_EXPECTED_PX;
            return <tr key={s.id} className="border-t border-white/5" data-id="element-1524" data-mp="1zgbcodh6ocqy">
                  <td className="px-4 py-2 text-gray-200" data-id="element-1525" data-mp="1uggls9xern5w">{s.id}</td>
                  <td className={`px-4 py-2 ${!r ? 'text-gray-500' : !r.decoded ? 'text-red-300' : ok ? 'text-emerald-300' : 'text-amber-300'}`} data-id="element-1526" data-mp="16p1f64ylmjou">{r ? describe(r) : 'Sin comprobar'}</td>
                  <td className="px-4 py-2 text-gray-300" data-id="element-1527" data-mp="1gnh6o91jfuyv">{!r || !r.decoded ? '—' : r.analyzable ? `${r.checked} · ${r.issues.length} con aviso` : 'píxeles no legibles'}</td>
                  <td className="max-w-[260px] truncate px-4 py-2 text-xs text-gray-500" data-id="element-1528" data-mp="1br0hdbilrak7">{s.url}</td>
                </tr>;
          })}
            {alternateSheetUploads.map((a, i) => <tr key={a.url} className="border-t border-white/5" data-id="element-1529" data-mp="2n65kggmnbb4c" data-mpi={i}>
                <td className="px-4 py-2 text-gray-400" data-id="element-1530" data-mp="2ulr13cxj6rfq">{a.id} (no se usa)</td>
                <td className="px-4 py-2 text-gray-500" data-id="element-1531" data-mp="2zngcwuskv94v">{alts[i] ? describe(alts[i]) : 'Sin comprobar'}</td>
                <td className="px-4 py-2 text-xs text-gray-500" data-id="element-1532" data-mp="2lnyvx1lix062">{a.note}</td>
                <td className="max-w-[260px] truncate px-4 py-2 text-xs text-gray-500" data-id="element-1533" data-mp="2pnpkqzrhp67y">{a.url}</td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </WorldSection>;
}